import { useCallback, useContext } from "react";
import { DateTime } from "luxon";
import { useParams } from "react-router";
import { useTranslation } from "react-i18next";

import { AppleMap } from "capitalrx-components-2.0";
import { formatDollars, formatPhoneNumber } from "capitalrx-utilities";
import { Button, Buttons, Color, HorizontalLine, LoadingGroup, LoadingSkeleton, Panel } from "capitalrx-components-2.0/lib/general";
import { Grid, Shelf, Spacing, Stack, useResponsive, useResponsiveDisplayMode } from "capitalrx-components-2.0/lib/layout";
import { Body, Heading, Semibold, Subheading, Subtitle, TextColor } from "capitalrx-components-2.0/lib/typography";

import { useCreateFavoritePharmacyMutation, useListFavoritePharmaciesQuery, useUpdateFavoritePharamcyMutation } from "@/App/api/pharmacy";
import { ContentPanel, PageWrapper } from "@/App/components";
import { ANALYTIC_EVENTS, logEvent } from "@/App/service/analyticsService";
import { MemberEligibilityContext } from "@/App/context";
import { getCustomerSupportNumber } from "@/App/utils/utils";
import { useGetPharmacyQuery } from "@/App/api/pharmacy";
import { useGetMemberClaim } from "@/App/api/member";
import { useNavigateClient } from "@/App/hooks";
import { NavigateButton } from "@/App/components/NavigateButton";
import { StackOrShelf } from "@/App/components/StackOrShelf";

import { ClaimInfoStack } from "../Claims";
import "./ClaimsDetails.scss";

export const PharmacyClaimDetailsPage = () => {
  const { t: translate, i18n } = useTranslation();
  const { id: claimId, personCode: personCode } = useParams();
  const { data: claim, isLoading: isLoadingMemberClaim } = useGetMemberClaim(+claimId, personCode);
  const { memberEligibility } = useContext(MemberEligibilityContext);
  const { data: pharmacy, isLoading: isLoadingPharmacyData } = useGetPharmacyQuery(claim?.pharmacy_nabp, !!claim);
  const {
    data: favoritePharmacies,
    isLoading: isLoadingFavoritePharmacies,
    isRefetching: isRefetchingFavoritePharmacies,
    refetch: refetchFavoritePharmacies,
  } = useListFavoritePharmaciesQuery();
  const { mutateAsync: updateFavoritePharmacy, isPending: isUpdatingFavoritePharmacy } = useUpdateFavoritePharamcyMutation();
  const { mutateAsync: createFavoritePharmacy, isPending: isCreatingFavoritePharmacy } = useCreateFavoritePharmacyMutation();
  const { isViewingDataholder } = useContext(MemberEligibilityContext);
  const navigate = useNavigateClient();

  const pharmacyProviderId = pharmacy?.provider_id;
  const isEmulating = !!sessionStorage.getItem("isEmulation");
  const isFavoritePharmacy = (favoritePharmacies?.results || []).map((pharm) => pharm.pharmacy.provider_id).includes(pharmacyProviderId);

  const toggleFavoritePharmacy = useCallback(async () => {
    try {
      if (isFavoritePharmacy) {
        await updateFavoritePharmacy(pharmacyProviderId);
      } else {
        await createFavoritePharmacy(pharmacyProviderId);
      }
      await refetchFavoritePharmacies();
    } catch (e) {
      const errorMessage = e?.data?.message
          || translate(`PRICING_DETAIL_PANELS.pharmacyDetails.${isFavoritePharmacy ? "remove" : "set"}FavoritePharmacy`);
      alert({
        message: errorMessage,
        type: "error",
        style: "toast",
      });
    }
  }, [isFavoritePharmacy, pharmacyProviderId]);

  const isFavoriteButtonDisabled = isViewingDataholder
      || isEmulating
      || isUpdatingFavoritePharmacy
      || isCreatingFavoritePharmacy
      || isLoadingFavoritePharmacies
      || isRefetchingFavoritePharmacies;

  const formattedDate = claim?.date_of_service ? DateTime.fromISO(claim?.date_of_service, { zone: "utc" }).toFormat("DDD") : "";

  const currentMode = useResponsiveDisplayMode();
  const { cardColumns, spacing, stackColumns } = useResponsive({
    cardColumns: {
      desktop: 6,
      tablet: 6,
      mobile: 12,
    },
    stackColumns: {
      desktop: 6,
      tablet: 12,
      mobile: 12,
    },
    spacing: {
      desktop: "large",
      tablet: "large",
      mobile: "mini",
    },
  });

  const onBackPressed = () => {
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.backButton);
    navigate(`/claims-history?claims_type=pharmacy&person_code=${personCode}`);
  };

  const onLookupCurrentPrice = () => {
    logEvent(ANALYTIC_EVENTS.CLAIM_HISTORY.lookupCurrentPrice);
    navigate(`/find-best-price/${claim.drug_id}`);
  };

  const formattedTotalAmount = formatDollars((+claim?.patient_pay_amount + +claim?.plan_pay_amount) || "0");
  const formattedPatientPayAmount = formatDollars(claim?.patient_pay_amount || "0");
  const formattedPlanPayAmount = formatDollars(claim?.plan_pay_amount || "0");

  let billingAddress = pharmacy?.address_line_1;
  if (pharmacy?.address_line_2) {
    billingAddress += `, ${pharmacy?.address_line_2}`;
  }
  if (pharmacy?.city && pharmacy?.state && pharmacy?.zip) {
    billingAddress += `, ${pharmacy?.city}, ${pharmacy?.state} ${pharmacy?.zip}`;
  }

  const customerSupportNumber = formatPhoneNumber(pharmacy?.phone);

  if (claim?.use_customer_support_hotline) {
    return getCustomerSupportNumber(memberEligibility);
  }

  let lookupButtonContainerClass = "current-price-button-container";
  if (currentMode === "mobile" || currentMode === "tablet") {
    lookupButtonContainerClass += " full-width";
  }

  let pharmacyHours = pharmacy?.hours || [];
  if(pharmacy?.hours && i18n.language !== "en") {
    pharmacyHours = pharmacy?.hours.map((day) => {
      const daySubstr = day.split(" - ");
      const translatedDayStart = translate(`CLAIMS.claimsDetailsPage.${daySubstr[0]}`);
      return translatedDayStart + " - " + daySubstr[1];
    });
  }

  const directMemberReimbursementIndicator = (claim?.direct_member_reimbursement_indicator ?? "0") !== "0" ? translate("CLAIMS.claimsDetailsPage.reimbursementClaim") : "";

  return (
    <PageWrapper>
      <ContentPanel
        title={
          <StackOrShelf alignment="top" spacing="mini">
            <Stack spacing="mini">
              <Shelf spacing="mini">
                <NavigateButton onClick={onBackPressed}>{translate("CLAIMS.claimsDetailsPage.back")}</NavigateButton>
                <Shelf.Spacer />
              </Shelf>
              <LoadingGroup loading={isLoadingMemberClaim || isLoadingPharmacyData}>
                <LoadingGroup.Placeholder
                  fallback={
                    <>
                      <LoadingSkeleton style={{ height: "30px", width: "175px", maxWidth: "100%" }} />
                      <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                      <LoadingSkeleton style={{ height: "20px", width: "250px", maxWidth: "100%" }} />
                    </>
                  }
                >
                  <Subtitle testId={`claim-title`}>
                    {claim?.drug_name}
                  </Subtitle>
                  <StackOrShelf spacing={spacing as Spacing} alignment="left">
                    <Body>{claim?.pharmacy_name}</Body>
                    <Body>{directMemberReimbursementIndicator}</Body>
                    <Shelf.Spacer />
                  </StackOrShelf>
                  <StackOrShelf spacing={spacing as Spacing} alignment="left">
                    <Shelf spacing="mini">
                      <Body><Semibold><TextColor appearance={Color.DarkGrey[400]}>ID:</TextColor></Semibold></Body>
                      <Body>{String(claimId).padStart(3, "0")}</Body>
                    </Shelf>
                    <Shelf spacing="mini">
                      <Body><Semibold><TextColor appearance={Color.DarkGrey[400]}>{translate("CLAIMS.claimsDetailsPage.claimReceived")}:</TextColor></Semibold></Body>
                      <Body>{formattedDate}</Body>
                    </Shelf>
                    <Shelf.Spacer />
                  </StackOrShelf>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Stack>
            <Shelf.Spacer />
          </StackOrShelf>
        }
      >
        <Grid>
          <Grid.Span columns={cardColumns}>
            <Stack spacing="large">
              <LoadingGroup loading={isLoadingMemberClaim || isLoadingPharmacyData}>
                <LoadingGroup.Placeholder
                  fallback={
                    <>
                      <Stack>
                        <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                        <Panel appearance="primary" className="member-portal-panel">
                          <Stack>
                            <Stack spacing="mini" alignment="left">
                              <LoadingSkeleton style={{ height: "24px", width: "100px" }} />
                              <LoadingSkeleton style={{ height: "20px", width: "250px" }} />
                            </Stack>
                            <Stack spacing="mini" alignment="left">
                              <LoadingSkeleton style={{ height: "24px", width: "100px" }} />
                              <LoadingSkeleton style={{ height: "20px", width: "250px" }} />
                            </Stack>
                            <Stack spacing="mini" alignment="left">
                              <LoadingSkeleton style={{ height: "24px", width: "75px" }} />
                              <LoadingSkeleton style={{ height: "20px", width: "100px" }} />
                            </Stack>
                          </Stack>
                        </Panel>
                      </Stack>
                      <Stack>
                        <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                        <Panel appearance="primary" className="member-portal-panel">
                          <Stack spacing="large">
                          <Stack>
                            <LoadingSkeleton style={{ height: "20px", width: "100%" }} />
                            <LoadingSkeleton style={{ height: "20px", width: "100%" }} />
                            <HorizontalLine />
                            <LoadingSkeleton style={{ height: "24px", width: "100%" }} />
                          </Stack>
                          <LoadingSkeleton style={{ height: "36px", width: "100%" }} />
                          </Stack>
                        </Panel>
                      </Stack>
                    </>
                  }
                >
                  <Stack>
                    <Heading>{translate("CLAIMS.claimsDetailsPage.prescriptionDetails")}</Heading>
                    <Panel appearance="primary" className="member-portal-panel">
                      <Grid>
                        <Grid.Span columns={12}>
                          <Subheading>{claim?.product_service_description}</Subheading>
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.type")}
                            body={claim?.drug_type}
                          />
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.quantity")}
                            body={claim?.quantity_dispensed}
                          />
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.daysSupply")}
                            body={claim?.days_supply}
                          />
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.dosage")}
                            body={claim?.dosage}
                          />
                        </Grid.Span>
                        <Grid.Span columns={stackColumns}>
                          <ClaimInfoStack
                            title={translate("CLAIMS.claimsDetailsPage.form")}
                            body={claim?.dosage_form}
                          />
                        </Grid.Span>
                      </Grid>
                    </Panel>
                  </Stack>
                  <Stack>
                    <Heading>{translate("CLAIMS.claimsDetailsPage.paymentSummary")}</Heading>
                    <Panel appearance="primary" className="member-portal-panel">
                      <Stack spacing="large">
                        <Stack>
                          <Shelf><Body>{translate("CLAIMS.claimsDetailsPage.totalAmount")}</Body><Shelf.Spacer />{formattedTotalAmount}</Shelf>
                          <Shelf><Body>{translate("CLAIMS.claimsDetailsPage.totalPlanPays")}</Body><Shelf.Spacer />-{formattedPlanPayAmount}</Shelf>
                          <HorizontalLine />
                          <Shelf><Subheading>{translate("CLAIMS.claimsDetailsPage.patientResponsibility")}</Subheading><Shelf.Spacer /><Subheading>{formattedPatientPayAmount}</Subheading></Shelf>
                        </Stack>
                        {claim?.drug_id && (
                          <div className={lookupButtonContainerClass}>
                            <Button appearance="secondary" onClick={onLookupCurrentPrice}>{translate("CLAIMS.claimsDetailsPage.lookupCurrentPrice")}</Button>
                          </div>
                        )}
                      </Stack>
                    </Panel>
                  </Stack>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Stack>
          </Grid.Span>
          <Grid.Span columns={cardColumns}>
            <Stack>
              <LoadingGroup loading={isLoadingMemberClaim || isLoadingPharmacyData}>
                <LoadingGroup.Placeholder
                  fallback={
                    <>
                      <Stack>
                        <LoadingSkeleton style={{ height: "24px", width: "150px" }} />
                        <Panel appearance="primary" className="member-portal-panel">
                          <Grid>
                            <Grid.Span columns={12}></Grid.Span>
                            <Grid.Span columns={stackColumns}>
                              <LoadingSkeleton style={{ height: "20px", width: "100px", maxWidth: "75%" }} />
                              <LoadingSkeleton style={{ height: "24px", width: "175px", maxWidth: "100%" }} />
                            </Grid.Span>
                            <Grid.Span columns={stackColumns}>
                              <LoadingSkeleton style={{ height: "20px", width: "100px", maxWidth: "75%" }} />
                              <LoadingSkeleton style={{ height: "24px", width: "175px", maxWidth: "100%" }} />
                            </Grid.Span>
                            <Grid.Span columns={stackColumns}>
                              <LoadingSkeleton style={{ height: "20px", width: "100px", maxWidth: "75%" }} />
                              <LoadingSkeleton style={{ height: "24px", width: "175px", maxWidth: "100%" }} />
                            </Grid.Span>
                            <Grid.Span columns={stackColumns}>
                              <LoadingSkeleton style={{ height: "20px", width: "100px", maxWidth: "75%" }} />
                              <LoadingSkeleton style={{ height: "24px", width: "175px", maxWidth: "100%" }} />
                            </Grid.Span>
                            <Grid.Span columns={stackColumns}>
                              <LoadingSkeleton style={{ height: "20px", width: "100px", maxWidth: "75%" }} />
                              <LoadingSkeleton style={{ height: "24px", width: "175px", maxWidth: "100%" }} />
                            </Grid.Span>
                            <Grid.Span columns={12}>
                              <LoadingSkeleton style={{ height: "250px", width: "100%" }} />
                            </Grid.Span>
                          </Grid>
                        </Panel>
                      </Stack>
                    </>
                  }
                >
                  <Heading>{translate("CLAIMS.claimsDetailsPage.pharmacyDetails")}</Heading>
                  <Panel appearance="primary" className="member-portal-panel">
                    <Grid>
                      <Grid.Span columns={!isFavoriteButtonDisabled ? 6 : 12}>
                        <ClaimInfoStack
                          title={translate("CLAIMS.claimsDetailsPage.pharmacyName")}
                          body={pharmacy?.name}
                        />
                      </Grid.Span>
                      {!isFavoriteButtonDisabled && (
                        <Grid.Span columns={6}>
                          <Button
                            className="unpadded-button"
                            testId="favorite-pharmacy"
                            appearance={Buttons.Functional.Tertiary.Large.Unpadded}
                            onClick={toggleFavoritePharmacy}
                            icon={isFavoritePharmacy ? "star-filled" : "star-empty"}
                          >
                            {isFavoritePharmacy && translate("PRICING_DETAIL_PANELS.pharmacyDetails.removeFavorite")}
                            {!isFavoritePharmacy && translate("PRICING_DETAIL_PANELS.pharmacyDetails.saveFavorite")}
                          </Button>
                        </Grid.Span>
                      )}
                      <Grid.Span columns={stackColumns}>
                        <ClaimInfoStack
                          title={translate("CLAIMS.claimsDetailsPage.address")}
                          body={billingAddress}
                        />
                      </Grid.Span>
                      <Grid.Span columns={stackColumns}>
                        <ClaimInfoStack
                          title={translate("CLAIMS.claimsDetailsPage.phone")}
                          body={customerSupportNumber}
                        />
                      </Grid.Span>
                      <Grid.Span columns={12}>
                        <ClaimInfoStack
                          title={translate("CLAIMS.claimsDetailsPage.hours")}
                          body={<Stack spacing="mini">{pharmacyHours.map((hour) => {
                            return <Body key={hour}>{hour}</Body>;
                          })}</Stack>}
                        />
                      </Grid.Span>
                      <Grid.Span columns={stackColumns}>
                        <ClaimInfoStack
                          title={translate("CLAIMS.claimsDetailsPage.fluShots")}
                          body={pharmacy?.services?.immunizations_provided_indicator}
                        />
                      </Grid.Span>
                      <Grid.Span columns={stackColumns}>
                        <ClaimInfoStack
                          title={translate("CLAIMS.claimsDetailsPage.driveThru")}
                          body={pharmacy?.services?.drive_up_window_indicator}
                        />
                      </Grid.Span>
                      {
                        pharmacy?.languages?.length > 0 && (
                          <Grid.Span columns={stackColumns}>
                            <ClaimInfoStack
                              title={translate("CLAIMS.claimsDetailsPage.languages")}
                              body={pharmacy?.languages}
                            />
                          </Grid.Span>
                        )
                      }
                      <Grid.Span columns={12}>
                        {pharmacy && (
                          <AppleMap
                            style={{
                              height: "100%",
                            }}
                            center={{
                              latitude: parseFloat(pharmacy.latitude),
                              longitude: parseFloat(pharmacy.longitude),
                            }}
                            locations={[{
                              latitude: parseFloat(pharmacy.latitude),
                              longitude: parseFloat(pharmacy.longitude),
                              title: pharmacy.name,
                              data: {
                                pharmacy,
                              },
                            }]}
                          />
                        )}
                      </Grid.Span>
                    </Grid>
                  </Panel>
                </LoadingGroup.Placeholder>
              </LoadingGroup>
            </Stack>
          </Grid.Span>
        </Grid>
      </ContentPanel>
    </PageWrapper>
  );
};
